import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import AdminHeader from '../components/AdminHeader';
import { useAuth } from '../AuthContext';

const AdminAssignmentTracker = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const [assignments, setAssignments] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [activeTab, setActiveTab] = useState('active');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    points: '',
    type: 'formative', // 'formative' or 'summative'
    status: 'pending', // 'pending', 'in_progress', 'in_progress_agent', 'completed'
    dueDate: '',
    className: '',
    priority: 'medium' // 'low', 'medium', 'high'
  });

  const classes = [
    'Advanced Biology F25',
    'Adv Algebra 2 - Rose',
    'Schwarz: English II',
    'Spanish 1 - Fall 2025',
    'Adv Design Applications'
  ];

  // Fetch assignments
  const fetchAssignments = async () => {
    try {
      const assignmentsSnapshot = await getDocs(collection(db, 'assignments'));
      const assignmentsList = assignmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      assignmentsList.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setAssignments(assignmentsList);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  // Create assignment
  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.points) return;

    try {
      await addDoc(collection(db, 'assignments'), {
        ...formData,
        points: parseFloat(formData.points),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      setFormData({
        title: '',
        description: '',
        points: '',
        type: 'formative',
        status: 'pending',
        dueDate: '',
        className: '',
        priority: 'medium'
      });
      setShowCreateForm(false);
      fetchAssignments();
    } catch (error) {
      console.error('Error creating assignment:', error);
    }
  };

  // Update assignment
  const handleUpdateAssignment = async (e) => {
    e.preventDefault();
    if (!editingAssignment) return;

    try {
      await updateDoc(doc(db, 'assignments', editingAssignment.id), {
        ...formData,
        points: parseFloat(formData.points),
        updatedAt: new Date().toISOString()
      });
      setEditingAssignment(null);
      setFormData({
        title: '',
        description: '',
        points: '',
        type: 'formative',
        status: 'pending',
        dueDate: '',
        className: '',
        priority: 'medium'
      });
      fetchAssignments();
    } catch (error) {
      console.error('Error updating assignment:', error);
    }
  };

  // Delete assignment
  const handleDeleteAssignment = async (assignmentId) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await deleteDoc(doc(db, 'assignments', assignmentId));
      fetchAssignments();
    } catch (error) {
      console.error('Error deleting assignment:', error);
    }
  };

  // Update assignment status
  const handleStatusChange = async (assignmentId, newStatus) => {
    try {
      await updateDoc(doc(db, 'assignments', assignmentId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      fetchAssignments();
    } catch (error) {
      console.error('Error updating assignment status:', error);
    }
  };

  // Start editing assignment
  const startEditing = (assignment) => {
    setEditingAssignment(assignment);
    setFormData({
      title: assignment.title || '',
      description: assignment.description || '',
      points: assignment.points?.toString() || '',
      type: assignment.type || 'formative',
      status: assignment.status || 'pending',
      dueDate: assignment.dueDate || '',
      className: assignment.className || '',
      priority: assignment.priority || 'medium'
    });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingAssignment(null);
    setFormData({
      title: '',
      description: '',
      points: '',
      type: 'formative',
      status: 'pending',
      dueDate: '',
      className: '',
      priority: 'medium'
    });
  };

  // Filter assignments by tab
  const filteredAssignments = assignments.filter(assignment => {
    switch (activeTab) {
      case 'active':
        return assignment.status !== 'completed';
      case 'completed':
        return assignment.status === 'completed';
      default:
        return true;
    }
  });

  // Fetch assignments on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchAssignments();
    }
  }, [isAuthenticated]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-600';
      case 'in_progress_agent':
        return 'bg-purple-600';
      case 'in_progress':
        return 'bg-blue-600';
      case 'pending':
        return 'bg-yellow-600';
      default:
        return 'bg-gray-600';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'in_progress_agent':
        return 'In Progress by Agent';
      case 'in_progress':
        return 'In Progress';
      case 'pending':
        return 'Pending';
      default:
        return status;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-400';
      case 'medium':
        return 'text-yellow-400';
      case 'low':
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">Assignment Tracker</h1>
              <p className="text-gray-400">Manage your assignments and track progress</p>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded font-medium transition-colors"
            >
              {showCreateForm ? 'Cancel' : '+ New Assignment'}
            </button>
          </div>

          {/* Create/Edit Form */}
          {(showCreateForm || editingAssignment) && (
            <div className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
              <h2 className="text-xl font-bold mb-6">
                {editingAssignment ? 'Edit Assignment' : 'Create New Assignment'}
              </h2>
              <form onSubmit={editingAssignment ? handleUpdateAssignment : handleCreateAssignment} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                      placeholder="Assignment title"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Class</label>
                    <select
                      value={formData.className}
                      onChange={(e) => setFormData({...formData, className: e.target.value})}
                      className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="">Select a class</option>
                      {classes.map(className => (
                        <option key={className} value={className}>{className}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    rows="3"
                    placeholder="Assignment description (optional)"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Points *</label>
                    <input
                      type="number"
                      value={formData.points}
                      onChange={(e) => setFormData({...formData, points: e.target.value})}
                      className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                      placeholder="100"
                      min="0"
                      step="0.5"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Type</label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="formative">Formative (35% weight)</option>
                      <option value="summative">Summative (65% weight)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Priority</label>
                    <select
                      value={formData.priority}
                      onChange={(e) => setFormData({...formData, priority: e.target.value})}
                      className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium mb-2">Due Date</label>
                    <input
                      type="date"
                      value={formData.dueDate}
                      onChange={(e) => setFormData({...formData, dueDate: e.target.value})}
                      className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                      className="w-full p-3 bg-[#212121] border border-[#4d4d4d] rounded text-white focus:outline-none focus:border-blue-500"
                    >
                      <option value="pending">Pending</option>
                      <option value="in_progress">In Progress</option>
                      <option value="in_progress_agent">In Progress by Agent</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>

                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded font-medium transition-colors"
                  >
                    {editingAssignment ? 'Update Assignment' : 'Create Assignment'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateForm(false);
                      cancelEditing();
                    }}
                    className="px-6 py-3 bg-gray-600 hover:bg-gray-700 rounded font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-[#4d4d4d]">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('active')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'active'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                Active Assignments ({assignments.filter(a => a.status !== 'completed').length})
              </button>
              <button
                onClick={() => setActiveTab('completed')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'completed'
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                }`}
              >
                Completed ({assignments.filter(a => a.status === 'completed').length})
              </button>
            </nav>
          </div>

          {/* Assignments List */}
          <div className="space-y-4">
            {filteredAssignments.length > 0 ? (
              filteredAssignments.map(assignment => (
                <div key={assignment.id} className="bg-[#1a1a1a] border border-[#4d4d4d] rounded-lg p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-xl font-bold">{assignment.title}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(assignment.status)}`}>
                          {getStatusText(assignment.status)}
                        </span>
                        <span className={`text-sm font-medium ${assignment.type === 'summative' ? 'text-red-400' : 'text-blue-400'}`}>
                          {assignment.type === 'summative' ? 'Summative' : 'Formative'}
                        </span>
                        <span className={`text-sm ${getPriorityColor(assignment.priority)}`}>
                          {assignment.priority.toUpperCase()}
                        </span>
                      </div>
                      {assignment.className && (
                        <p className="text-sm text-gray-400 mb-2">{assignment.className}</p>
                      )}
                      {assignment.description && (
                        <p className="text-gray-300 mb-2">{assignment.description}</p>
                      )}
                      <div className="flex items-center space-x-4 text-sm text-gray-400">
                        <span>{assignment.points} points</span>
                        {assignment.dueDate && (
                          <span>Due: {new Date(assignment.dueDate).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {assignment.status !== 'completed' && (
                        <>
                          <button
                            onClick={() => handleStatusChange(assignment.id, 'in_progress')}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 rounded text-sm transition-colors"
                          >
                            Start
                          </button>
                          <button
                            onClick={() => handleStatusChange(assignment.id, 'in_progress_agent')}
                            className="px-3 py-1 bg-purple-600 hover:bg-purple-700 rounded text-sm transition-colors"
                          >
                            Agent
                          </button>
                          <button
                            onClick={() => handleStatusChange(assignment.id, 'completed')}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 rounded text-sm transition-colors"
                          >
                            Complete
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => startEditing(assignment)}
                        className="px-3 py-1 bg-yellow-600 hover:bg-yellow-700 rounded text-sm transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteAssignment(assignment.id)}
                        className="px-3 py-1 bg-red-600 hover:bg-red-700 rounded text-sm transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-[#2a2a2a] rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                    <path fillRule="evenodd" d="M4 5a2 2 0 012-2v1a1 1 0 002 0V3a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3z" clipRule="evenodd"/>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">No assignments found</h3>
                <p className="text-gray-400">
                  {activeTab === 'active' ? 'Create your first assignment to get started!' : 'No completed assignments yet.'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAssignmentTracker;
